import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useMatch, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';

import { FeatureFlagsManager } from '../../utils/core/feature-flags-manager';
import { DroneLoader } from '../components/drone-loader/drone-loader';
import { UnifiedProjectList } from '../components/projects-views/unified-project-list';
import { useAppContext } from '../state/app-state';
import { getProductName } from '../state/theme-state';
import { Center } from '../styles/center';

type SelectedProjectInfo = {
  id: string;
  name: string;
};

type ProjectsOutletContext = {
  selectedProject?: SelectedProjectInfo;
  setSelectedProject: React.Dispatch<React.SetStateAction<SelectedProjectInfo | undefined>>;
};

const useProjectsLayoutContext = () => useOutletContext<ProjectsOutletContext>();

const ProjectsLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [selectedProject, setSelectedProject] = React.useState<SelectedProjectInfo | undefined>();

  const projectPageTitles = React.useMemo(
    () => ({
      '': t('menu.projects.title', { ns: 'navigation' }),
      viewer: t('menu.skyview.title', { ns: 'navigation' }),
      timeline: t('menu.timeline.title', { ns: 'navigation' }),
      geodata: t('menu.cloudProcessing.title', { ns: 'navigation' }),
      filemanager: t('menu.fileManager.title', { ns: 'navigation' }),
      users: t('menu.projectAttendee.title', { ns: 'navigation' }),
      email: t('menu.email.title', { ns: 'navigation' }),
      transfer: t('menu.transfer.title', { ns: 'navigation' }),
      integrations: t('menu.integrations.title', { ns: 'navigation' }),
      settings: t('menu.settings.title', { ns: 'navigation' }),
    }),
    [t],
  );

  const pageTitle = React.useMemo(() => {
    if (!selectedProject) {
      return projectPageTitles[''];
    }
    const match = location.pathname.match(/^\/projects\/[^/]+\/(.*)$/);
    const segment = (match?.[1] ?? '').split('/')[0] ?? '';
    const page =
      projectPageTitles[segment as keyof typeof projectPageTitles] ?? projectPageTitles[''];
    return [page, selectedProject.name].join(' - ');
  }, [location.pathname, selectedProject, projectPageTitles]);

  React.useEffect(() => {
    document.title = selectedProject ? selectedProject.name : getProductName();
    return () => {
      document.title = getProductName();
    };
  }, [selectedProject]);

  const contextValue = React.useMemo(
    () => ({ selectedProject, setSelectedProject }),
    [selectedProject],
  );

  const isSkyViewRoute = Boolean(useMatch('/projects/:projectId/viewer'));
  const isGcpViewerRoute = Boolean(useMatch('/projects/:projectId/geodata/:geodataId/viewer'));
  const isGeodataUploadRoute = Boolean(useMatch('/projects/:projectId/geodata/upload'));
  const isFileManagerRoute = Boolean(useMatch('/projects/:projectId/filemanager'));

  const isFullBleedRoute = useMemo(() => {
    return isSkyViewRoute || isGcpViewerRoute || isFileManagerRoute || isGeodataUploadRoute;
  }, [isSkyViewRoute, isGcpViewerRoute, isFileManagerRoute, isGeodataUploadRoute]);

  // Always render <Outlet> at the same position in the component tree so that
  // ProjectLayout (and its descendants) are never remounted when switching
  // between full-bleed routes (SkyView, FileManager) and normal routes.
  // Conditional wrapping (e.g. `if (fullBleed) return <Outlet>`) would change
  // the component type above <Outlet>, causing React to unmount/remount the
  // entire subtree and trigger redundant API fetches.
  return (
    <OuterShell $fullBleed={isFullBleedRoute}>
      <InnerShell $fullBleed={isFullBleedRoute}>
        {!isFullBleedRoute && pageTitle && <PageTitle>{pageTitle}</PageTitle>}
        <Outlet context={contextValue} />
      </InnerShell>
    </OuterShell>
  );
};

const ProjectsPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAppContext();
  const { setSelectedProject } = useProjectsLayoutContext();

  React.useEffect(() => {
    setSelectedProject(undefined);
  }, [setSelectedProject]);

  if (!currentUser?.selectedRole) {
    return FeatureFlagsManager.instance.isEnabled('SK-6974-drone-loader') ? (
      <DroneLoader mode="page-content" />
    ) : (
      <Center>{t('oneMoment', { ns: 'common' })}</Center>
    );
  }

  return <UnifiedProjectList />;
};

const ProjectsListRoute = () => {
  return <ProjectsPage />;
};

export { ProjectsLayout, ProjectsListRoute, ProjectsPage, useProjectsLayoutContext };

/**
 * Outer centering shell — replicates PageContainer's Outer for normal routes;
 * passes through at full height for full-bleed routes (SkyView, FileManager, etc.).
 */
const OuterShell = styled.div<{ $fullBleed: boolean }>`
  ${(props) =>
    props.$fullBleed
      ? 'height: 100%;'
      : 'display: flex; justify-content: center; padding: 50px 0 20px;'}
`;

/**
 * Inner content shell — replicates PageContainer's Inner (white card) for normal routes;
 * passes through at full height for full-bleed routes.
 */
const InnerShell = styled.div<{ $fullBleed: boolean }>`
  ${(props) =>
    props.$fullBleed
      ? 'height: 100%;'
      : `
    position: relative;
    background-color: #ffffff;
    padding: 25px;
    width: clamp(600px, 75%, 1200px);
    margin: 0 auto;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
    height: 100%;
  `}
`;

const PageTitle = styled.h1`
  margin: 0 0 20px;
  padding: 0;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 14pt;
  font-weight: 600;
  color: #333333;
`;
